import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Alert, Spinner, Row, Col, Table, Pagination, Card, Nav, Badge } from 'react-bootstrap';
import './AdminPanel.css';
import '../assets/loading.css';
import { useNavigate } from 'react-router-dom';

function AdminPanel() {
    const [loading, setLoading] = useState(true);
    const [authorized, setAuthorized] = useState(false);
    const [message, setMessage] = useState(null);
    const [activeTab, setActiveTab] = useState('users');
    
    // User management states
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        email: '',
        rank: 'guest',
    });
    const [users, setUsers] = useState([]);
    const [deleteUsername, setDeleteUsername] = useState('');
    
    // System message states
    const [systemMessage, setSystemMessage] = useState('');
    const [currentSystemMessage, setCurrentSystemMessage] = useState('');
    
    // Logs states
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({
        total: 0,
        pages: 0,
        current_page: 1,
        limit: 10
    });
    const [logFilters, setLogFilters] = useState({
        username: '',
        action: '',
        item_type: '',
        from_date: '',
        to_date: ''
    });
    
    const navigate = useNavigate();

    useEffect(() => {
        fetch('https://usscfedora.org/check_admin.php', {
            method: 'GET',
            credentials: 'include',
        })
        .then(response => response.json())
        .then(data => {
            if (data.status === 'success') {
                setAuthorized(true);
                fetchUsers();
                fetchSystemMessage();
            } else {
                navigate('/401');
            }
            setLoading(false);
        })
        .catch(error => {
            console.error('Error verifying admin:', error);
            setLoading(false);
            navigate('/401');
        });
    }, [navigate]);

    // Fetch logs when active tab changes to logs or pagination changes
    useEffect(() => {
        if (authorized && activeTab === 'logs') {
            fetchLogs();
        }
    }, [authorized, activeTab, pagination.current_page]);

    const fetchUsers = () => {
        fetch('https://usscfedora.org/get_users.php', {
            method: 'GET',
            credentials: 'include',
        })
        .then(response => response.json())
        .then(data => {
            setUsers(data.users || []);
        })
        .catch(error => {
            console.error('Error fetching users:', error);
        });
    };

    const fetchSystemMessage = () => {
        fetch('https://usscfedora.org/get_system_notifications.php', {
            method: 'GET',
            credentials: 'include',
        })
        .then(response => response.json())
        .then(data => {
            if (data.notifications && data.notifications.length > 0) {
                setCurrentSystemMessage(data.notifications[0].message);
            }
        })
        .catch(error => {
            console.error('Error fetching system notifications:', error);
        });
    };

    const fetchLogs = () => {
        setLoading(true);
        
        let url = `https://usscfedora.org/log_activity.php?page=${pagination.current_page}&limit=${pagination.limit}`;
        
        // Add filters to URL if they exist
        Object.keys(logFilters).forEach(key => {
            if (logFilters[key]) {
                url += `&${key}=${encodeURIComponent(logFilters[key])}`;
            }
        });
        
        fetch(url, {
            method: 'GET',
            credentials: 'include',
        })
        .then(response => response.json())
        .then(data => {
            if (data.status === 'success') {
                setLogs(data.logs);
                setPagination(data.pagination);
            } else {
                console.error('Error fetching logs:', data.message);
            }
            setLoading(false);
        })
        .catch(error => {
            console.error('Error fetching logs:', error);
            setLoading(false);
        });
    };

    // User management handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewUser(prevState => ({ ...prevState, [name]: value }));
    };

    const handleDeleteInputChange = (e) => {
        setDeleteUsername(e.target.value);
    };

    const handleCreateUser = () => {
        setLoading(true);
        fetch('https://usscfedora.org/create_user.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(newUser),
        })
        .then(response => response.json())
        .then(data => {
            setMessage(data.message);
            setLoading(false);
            if (data.status === 'success') {
                setNewUser({
                    username: '',
                    password: '',
                    email: '',
                    rank: 'guest',
                });
                fetchUsers();
            }
        })
        .catch(error => {
            console.error('Error creating user:', error);
            setLoading(false);
        });
    };

    const handleDeleteUser = () => {
        setLoading(true);
        fetch('https://usscfedora.org/delete_user.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ username: deleteUsername }),
        })
        .then(response => response.json())
        .then(data => {
            setMessage(data.message);
            setLoading(false);
            if (data.status === 'success') {
                setDeleteUsername('');
                fetchUsers();
            }
        })
        .catch(error => {
            console.error('Error deleting user:', error);
            setLoading(false);
        });
    };

    // System message handlers
    const handleSystemMessageChange = (e) => {
        setSystemMessage(e.target.value);
    };

    const handleUpdateSystemMessage = () => {
        setLoading(true);
        fetch('https://usscfedora.org/update_system_message.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ message: systemMessage }),
        })
        .then(response => response.json())
        .then(data => {
            setMessage(data.message);
            setLoading(false);
            if (data.status === 'success') {
                setCurrentSystemMessage(systemMessage);
                setSystemMessage('');
            }
        })
        .catch(error => {
            console.error('Error updating system message:', error);
            setLoading(false);
        });
    };

    // Logs handlers
    const handleLogFilterChange = (e) => {
        const { name, value } = e.target;
        setLogFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleLogFilterSubmit = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, current_page: 1 }));
        fetchLogs();
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, current_page: page }));
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const getActionColor = (action) => {
        switch(action.toLowerCase()) {
            case 'delete':
                return 'danger';
            case 'create':
                return 'success';
            case 'update':
                return 'warning';
            default:
                return 'info';
        }
    };

    const getItemTypeIcon = (itemType) => {
        switch(itemType.toLowerCase()) {
            case 'project':
                return '📁';
            case 'exploit':
                return '🛠️';
            case 'challenge':
                return '🏆';
            default:
                return '📄';
        }
    };

    if (loading && !authorized) {
        return <div className="loading-spinner"></div>;
    }

    if (!authorized) {
        return null;
    }

    return (
        <Container fluid className="admin-panel-container mt-5">
            <h2 className="admin-panel-title">Admin Control Panel</h2>
            {message && <Alert variant="info" dismissible onClose={() => setMessage(null)}>{message}</Alert>}
            
            <Nav variant="tabs" className="admin-panel-tabs mb-4" activeKey={activeTab} onSelect={(tab) => setActiveTab(tab)}>
                <Nav.Item>
                    <Nav.Link eventKey="users" className="admin-panel-tab">User Management</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="notifications" className="admin-panel-tab">System Notifications</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="logs" className="admin-panel-tab">Activity Logs</Nav.Link>
                </Nav.Item>
            </Nav>
            
            {activeTab === 'users' && (
                <Row>
                    <Col md={6}>
                        <Card className="admin-panel-card">
                            <Card.Header className="admin-panel-card-header">Create User</Card.Header>
                            <Card.Body>
                                <Form>
                                    <Form.Group controlId="formUsername">
                                        <Form.Label className="admin-panel-form-label">Username</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            name="username" 
                                            value={newUser.username} 
                                            onChange={handleInputChange} 
                                            placeholder="Enter username" 
                                            className="admin-panel-input"
                                        />
                                    </Form.Group>

                                    <Form.Group controlId="formPassword" className="mt-3">
                                        <Form.Label className="admin-panel-form-label">Password</Form.Label>
                                        <Form.Control 
                                            type="password" 
                                            name="password" 
                                            value={newUser.password} 
                                            onChange={handleInputChange} 
                                            placeholder="Enter password" 
                                            className="admin-panel-input"
                                        />
                                    </Form.Group>

                                    <Form.Group controlId="formEmail" className="mt-3">
                                        <Form.Label className="admin-panel-form-label">Email</Form.Label>
                                        <Form.Control 
                                            type="email" 
                                            name="email" 
                                            value={newUser.email} 
                                            onChange={handleInputChange} 
                                            placeholder="Enter email" 
                                            className="admin-panel-input"
                                        />
                                    </Form.Group>

                                    <Form.Group controlId="formRank" className="mt-3">
                                        <Form.Label className="admin-panel-form-label">Rank</Form.Label>
                                        <Form.Control 
                                            as="select" 
                                            name="rank" 
                                            value={newUser.rank} 
                                            onChange={handleInputChange}
                                            className="admin-panel-select"
                                        >
                                            <option value="guest">Guest</option>
                                            <option value="member">Member</option>
                                            <option value="root">Root</option>
                                        </Form.Control>
                                    </Form.Group>

                                    <Button variant="primary" onClick={handleCreateUser} className="mt-4 admin-panel-button">
                                        {loading ? <Spinner animation="border" size="sm" /> : 'Create User'}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="admin-panel-card">
                            <Card.Header className="admin-panel-card-header">Delete User</Card.Header>
                            <Card.Body>
                                <Form>
                                    <Form.Group controlId="formDeleteUsername">
                                        <Form.Label className="admin-panel-form-label">Select User to Delete</Form.Label>
                                        <Form.Control 
                                            as="select" 
                                            value={deleteUsername} 
                                            onChange={handleDeleteInputChange}
                                            className="admin-panel-select"
                                        >
                                            <option value="">Select a user</option>
                                            {users.map((user) => (
                                                <option key={user.username} value={user.username}>
                                                    {user.username} ({user.rank})
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>

                                    <Button variant="danger" onClick={handleDeleteUser} className="mt-4 admin-panel-button-danger">
                                        {loading ? <Spinner animation="border" size="sm" /> : 'Delete User'}
                                    </Button>
                                </Form>
                                
                                <div className="mt-4">
                                    <h5 className="admin-panel-subtitle">Current Users</h5>
                                    <div className="admin-panel-users-list">
                                        {users.map((user) => (
                                            <div key={user.username} className="admin-panel-user-item">
                                                <span className="admin-panel-user-name">{user.username}</span>
                                                <Badge 
                                                    bg={user.rank === 'root' ? 'danger' : (user.rank === 'member' ? 'primary' : 'secondary')}
                                                    className="admin-panel-user-badge"
                                                >
                                                    {user.rank}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
            
            {activeTab === 'notifications' && (
                <Row>
                    <Col md={12}>
                        <Card className="admin-panel-card">
                            <Card.Header className="admin-panel-card-header">System Notifications</Card.Header>
                            <Card.Body>
                                <Form>
                                    <Form.Group controlId="formSystemMessage">
                                        <Form.Label className="admin-panel-form-label">Current System Message</Form.Label>
                                        <div className="admin-panel-current-message">
                                            {currentSystemMessage || 'No current system message.'}
                                        </div>
                                        
                                        <Form.Label className="admin-panel-form-label mt-4">New System Message</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            value={systemMessage} 
                                            onChange={handleSystemMessageChange} 
                                            placeholder="Enter new system message" 
                                            className="admin-panel-input"
                                        />
                                    </Form.Group>

                                    <Button variant="primary" onClick={handleUpdateSystemMessage} className="mt-3 admin-panel-button">
                                        {loading ? <Spinner animation="border" size="sm" /> : 'Update System Message'}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
            
            {activeTab === 'logs' && (
                <Row>
                    <Col md={12}>
                        <Card className="admin-panel-card mb-4">
                            <Card.Header className="admin-panel-card-header">Log Filters</Card.Header>
                            <Card.Body>
                                <Form onSubmit={handleLogFilterSubmit}>
                                    <Row className="mb-3">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="admin-panel-form-label">Username</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="username"
                                                    value={logFilters.username}
                                                    onChange={handleLogFilterChange}
                                                    placeholder="Filter by username"
                                                    className="admin-panel-input"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="admin-panel-form-label">Action</Form.Label>
                                                <Form.Select
                                                    name="action"
                                                    value={logFilters.action}
                                                    onChange={handleLogFilterChange}
                                                    className="admin-panel-select"
                                                >
                                                    <option value="">All Actions</option>
                                                    <option value="create">Create</option>
                                                    <option value="update">Update</option>
                                                    <option value="delete">Delete</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="admin-panel-form-label">Item Type</Form.Label>
                                                <Form.Select
                                                    name="item_type"
                                                    value={logFilters.item_type}
                                                    onChange={handleLogFilterChange}
                                                    className="admin-panel-select"
                                                >
                                                    <option value="">All Types</option>
                                                    <option value="project">Project</option>
                                                    <option value="exploit">Exploit</option>
                                                    <option value="challenge">Challenge</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Button 
                                                variant="primary" 
                                                type="submit" 
                                                className="mt-4 w-100 admin-panel-button"
                                            >
                                                Apply Filters
                                            </Button>
                                        </Col>
                                    </Row>
                                    
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="admin-panel-form-label">From Date</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="from_date"
                                                    value={logFilters.from_date}
                                                    onChange={handleLogFilterChange}
                                                    className="admin-panel-input"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="admin-panel-form-label">To Date</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="to_date"
                                                    value={logFilters.to_date}
                                                    onChange={handleLogFilterChange}
                                                    className="admin-panel-input"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>
                        
                        {loading ? (
                            <div className="loading-spinner"></div>
                        ) : (
                            <>
                                <div className="table-responsive admin-panel-logs-container">
                                    <Table bordered hover className="admin-panel-logs-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Timestamp</th>
                                                <th>Username</th>
                                                <th>Action</th>
                                                <th>Item Type</th>
                                                <th>Item Name</th>
                                                <th>Details</th>
                                                <th>IP Address</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.length > 0 ? (
                                                logs.map(log => (
                                                    <tr key={log.id}>
                                                        <td>{log.id}</td>
                                                        <td>{formatDate(log.timestamp)}</td>
                                                        <td><strong>{log.username}</strong></td>
                                                        <td>
                                                            <Badge bg={getActionColor(log.action)}>
                                                                {log.action.toUpperCase()}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <span>{getItemTypeIcon(log.item_type)} {log.item_type}</span>
                                                        </td>
                                                        <td>{log.item_name}</td>
                                                        <td>{log.details}</td>
                                                        <td>{log.ip_address}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center">No logs found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {pagination.pages > 1 && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <Pagination className="admin-panel-pagination">
                                            <Pagination.First 
                                                onClick={() => handlePageChange(1)} 
                                                disabled={pagination.current_page === 1}
                                            />
                                            <Pagination.Prev 
                                                onClick={() => handlePageChange(pagination.current_page - 1)} 
                                                disabled={pagination.current_page === 1}
                                            />
                                            
                                            {[...Array(pagination.pages)].map((_, i) => {
                                                // Show limited page links for better UI
                                                if (
                                                    i === 0 || 
                                                    i === pagination.pages - 1 || 
                                                    (i >= pagination.current_page - 2 && i <= pagination.current_page + 2)
                                                ) {
                                                    return (
                                                        <Pagination.Item 
                                                            key={i + 1} 
                                                            active={i + 1 === pagination.current_page}
                                                            onClick={() => handlePageChange(i + 1)}
                                                        >
                                                            {i + 1}
                                                        </Pagination.Item>
                                                    );
                                                } else if (
                                                    i === pagination.current_page - 3 || 
                                                    i === pagination.current_page + 3
                                                ) {
                                                    return <Pagination.Ellipsis key={i} />;
                                                }
                                                return null;
                                            })}
                                            
                                            <Pagination.Next 
                                                onClick={() => handlePageChange(pagination.current_page + 1)} 
                                                disabled={pagination.current_page === pagination.pages}
                                            />
                                            <Pagination.Last 
                                                onClick={() => handlePageChange(pagination.pages)} 
                                                disabled={pagination.current_page === pagination.pages}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                                
                                <div className="text-center mt-3 admin-panel-logs-stats">
                                    <small>
                                        Showing {logs.length} of {pagination.total} logs | 
                                        Page {pagination.current_page} of {pagination.pages || 1}
                                    </small>
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            )}
        </Container>
    );
}

export default AdminPanel;